/* eslint-disable no-undef */
import React, { useState, useEffect, useMemo } from 'react';
import TradeCommentsModal from './TradeCommentsModal';
import { Button, Modal, Table, Tag, Space, Spin, Input, Checkbox } from 'antd';
import { toast } from 'react-toastify';
import TakeProfitPopup from './TakeProfitPopup';
import StopLossPopup from './StopLossPopup';
import OrderForm from './OrderForm';
import LetterEmoji from './LetterEmoji';
import { BASE_URL } from '../api';
import { openOrderSuccess } from './OrderSuccessModal';
import { cleanBybitFromMessage } from '../utils/messageUtils';


/* eslint-disable no-undef */
// import { Spin } from "antd"; // Import Spin for loading state

// Mock BASE_URL for demo - replace with your actual BASE_URL
// const BASE_URL = 'http://localhost:5000';

const OrdersTable = ({ token, orderType, type, orderID, onOrderUpdate, refreshKey, active, side, filterStatus, filterMode = 'active' }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);
  const [userRole, setUserRole] = useState('staff'); // Will be determined from API response
  const [todoCount, setTodoCount] = useState({});
  const [cancelModal, setCancelModal] = useState({ visible: false, order: null });
  const [cancelComment, setCancelComment] = useState(''); // State for cancel comment
  const [cancelLoadingMap, setCancelLoadingMap] = useState({}); // { [order._id]: boolean }
  const [tpPopup, setTpPopup] = useState({ visible: false, order: null, currentPrice: '' });
  const [slPopup, setSlPopup] = useState({ visible: false, order: null, currentPrice: '' });
  const [orderFormVisible, setOrderFormVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [dcaOrder, setDcaOrder] = useState(null); // For DCA order data
  const [commentsModal, setCommentsModal] = useState({ visible: false, order: null });
  // Use TP/SL counts from API
  const [tpActiveCounts, setTpActiveCounts] = useState({}); // { orderId: count }
  const [slActiveCounts, setSlActiveCounts] = useState({});
  
  // Helper function to get emoji for order based on tradeId
  const getOrderEmoji = (record, allOrders) => {
    // Use tradeId from API response and take the last letter
    if (record.tradeId) {
      const tradeIdStr = record.tradeId.toString();
      const lastLetter = tradeIdStr.slice(-1);
      return <LetterEmoji text={lastLetter} />;
    }
    
    // Fallback to using _id if tradeId is not available
    return <LetterEmoji text={record._id} />;
  };

  // Helper function to check if order should hide action buttons
  const shouldHideActionButtons = (record) => {
    // Check if is_dca_order is true
    if (!record.is_dca_order) return false;
    
    // Check if createdAt is after 3:30 PM IST on October 9th, 2025
    if (!record.createdAt) return false;
    
    const orderDate = new Date(record.createdAt);
    const istDate = new Date(orderDate.getTime() + (5.5 * 60 * 60 * 1000)); // Convert to IST
    
    // Check if it's October 9th, 2025
    const targetDate = new Date('2025-10-09T15:30:00Z'); // 3:30 PM IST = 10:00 AM UTC
    const targetDateInIST = new Date(targetDate.getTime() + (5.5 * 60 * 60 * 1000));
    
    // Check if the date is October 9th, 2025 and time is after 3:30 PM IST
    const isSameDate = istDate.getUTCFullYear() === 2025 && 
                       istDate.getUTCMonth() === 9 && // October is month 9 (0-indexed)
                       istDate.getUTCDate() === 9;
    
    const isAfter330PM = istDate.getUTCHours() > 15 || 
                         (istDate.getUTCHours() === 15 && istDate.getUTCMinutes() >= 30);
    
    return isSameDate && isAfter330PM;
  };
  
  // Function to fetch order statuses silently in the background
  const fetchOrderStatuses = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/orders/fetchorderstatus`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        // After updating statuses, refresh the orders list without loading state
        fetchOrders(true, true); // Pass true for isAuto and skipLoading
      }
    } catch (error) {
      console.error('Error fetching order statuses:', error);
    }
  };

  // // Set up interval for status updates
  // useEffect(() => {
  //   // Initial fetch when orderType changes
  //   fetchOrders();
    
  //   // Set up 10-second interval for auto-refresh
  //   const intervalId = setInterval(() => {
  //     fetchOrders(true, true); // Auto-refresh with current orderType
  //   }, 10000);

  //   // Cleanup interval on component unmount
  //   return () => clearInterval(intervalId);
  // }, [orderType]); // Include orderType to re-fetch when it changes

  
useEffect(() => {
  // Initial fetch when orderType changes
  fetchOrders();

  // Set up two intervals:
  // 1) shortIntervalId - every 10 seconds call fetchOrders(true, true) to auto-refresh visible table
  // 2) longIntervalId - every 24 hours call fetchOrderStatuses() to run the background status sync
  const shortIntervalId = setInterval(() => {
    fetchOrders(true, true); // Auto-refresh with current orderType, skip loading UI
  }, 10000); // 10 seconds

  const longIntervalId = setInterval(() => {
    fetchOrderStatuses(); // Background status updates (24 hours)
  }, 86400000); // 24 hours

  // Cleanup intervals on component unmount or when dependencies change
  return () => {
    clearInterval(shortIntervalId);
    clearInterval(longIntervalId);
  };
}, [orderType, token, type, orderID, refreshKey, active, side, filterStatus]); // Include all dependencies that should

  // Update TP/SL counts whenever orders update
  useEffect(() => {
    if (!orders || !orders.length) return;
    
    const tpCounts = {};
    const slCounts = {};
    
    orders.forEach(order => {
      if (order.orderNature === 'normal') {
        tpCounts[order._id] = order.tpCount || 0;
        slCounts[order._id] = order.slCount || 0;
      }
    });

    setTpActiveCounts(tpCounts);
    setSlActiveCounts(slCounts);
  }, [orders]);
  // Helper to refresh TP/SL counts for a single order
  const refreshTpSlCountForOrder = async (orderId) => {
    if (!orderId) return;
    // TP
    try {
      const resTp = await fetch(`${BASE_URL}/api/orders/tp/${orderId}`, { credentials: 'include' });
      const dataTp = await resTp.json();
      setTpActiveCounts(prev => ({
        ...prev,
        [orderId]: Array.isArray(dataTp) ? dataTp.filter(tp => {
          const status = (tp.orderStatus || '').toLowerCase();
          return status !== 'filled' && status !== 'cancelled' && status !== 'failed';
        }).length : 0
      }));
    } catch {}
    // SL
    try {
      const resSl = await fetch(`${BASE_URL}/api/orders/sl/${orderId}`, { credentials: 'include' });
      const dataSl = await resSl.json();
      setSlActiveCounts(prev => ({
        ...prev,
        [orderId]: Array.isArray(dataSl) ? dataSl.filter(sl => {
          const status = (sl.orderStatus || '').toLowerCase();
          return status !== 'filled' && status !== 'cancelled' && status !== 'failed';
        }).length : 0
      }));
    } catch {}
  };
  
  const handleCommentUpdate = (order, { todoCount }) => {
    setTodoCount(prev => ({ ...prev, [order._id]: todoCount }));
  };
  // ✅ REMOVED: Legacy cookie-based user detection - now using JWT auth
  // const getCurrentUser = () => {
  //   const match = document.cookie.match(/(?:^|; )username=([^;]*)/);
  //   return match && match[1] ? decodeURIComponent(match[1]) : '';
  // };

  // // Fetch orders with filtering
  // const fetchOrders = async (isAuto = false, skipLoading = false) => {
  //   if (!isAuto && !skipLoading) {
  //     setLoading(true); // Only show loading on manual refresh and when not skipped
  //   }
  //   try {
  //     let url = `${BASE_URL}/api/orders`;
  //     const params = new URLSearchParams();
      
  //     // Always include orderType parameter, even for auto-refresh
  //     params.append('orderNature', orderType || 'normal');
      
  //     if (token) params.append('token', token);
  //     if (type) params.append('type', type); // Market, Limit
  //     if (orderID) params.append('orderID', orderID);
  //     if (typeof active !== 'undefined' && active !== null) {
  //       params.append('active', active ? '1' : '0');
  //     }
      
  //     url += `?${params.toString()}`;
  //     const res = await fetch(url, { credentials: 'include' });
  //     let data = await res.json();
      
  //     // Determine user role based on data structure
  //     if (data.length > 0) {
  //       // If remainingQuantity exists and is not null, user is admin
  //       const hasRemainingQuantity = data.some(order => 
  //         order.remainingQuantity !== null && order.remainingQuantity !== undefined
  //       );
  //     //   setUserRole(hasRemainingQuantity ? 'admin' : 'staff');
  //     }

  //     // Update status for active orders (only for orders with orderId)
  //     // await Promise.all(data.map(async (order, idx) => {
  //     //   if (( order.orderStatus === 'placed' || order.orderStatus === "submitted" || order.orderStatus =="New")  && order.orderId) {
  //     //     try {
  //     //       const statusRes = await fetch(`${BASE_URL}/api/orders/status/${order.orderId}`, { credentials: 'include' });
  //     //       const statusData = await statusRes.json();
  //     //       if (statusData.status && statusData.status !== order.orderStatus) {
  //     //         data[idx].orderStatus = statusData.status;
  //     //       }
  //     //     } catch (e) {
  //     //       console.error('Error fetching order status:', e);
  //     //     }
  //     //   }
  //     // }));

  //     // Filter by side if prop is provided
  //     let filteredData = data;
  //     if (side) {
  //       filteredData = filteredData.filter(order => order.side === side);
  //     }
  //     // Filter by order status if filterStatus prop is provided
  //     if (filterStatus) {
  //       filteredData = filteredData.filter(order => {
  //         if (!order.orderStatus) return false;
  //         // Case-insensitive match
  //         return order.orderStatus.toLowerCase() === filterStatus.toLowerCase();
  //       });
  //     }

  //     setOrders(filteredData);
  //     if (onOrderUpdate) {
  //       onOrderUpdate(filteredData);
  //     }
  //   } catch (err) {
  //     console.error('Error fetching orders:', err);
  //     toast.error('Failed to fetch orders');
  //   }finally {
  //   setLoading(false); // Make sure this is here
  // }
  // };

  // Also update the fetchOrders function to properly handle the orderType parameter:

const fetchOrders = async (isAuto = false, skipLoading = false) => {
  if (!isAuto && !skipLoading) {
    setLoading(true); // Only show loading on manual refresh and when not skipped
  }
  try {
    let url = `${BASE_URL}/api/orders`;
    const params = new URLSearchParams();
    
    // Only include orderType parameter if it's explicitly provided
    if (orderType) {
      params.append('orderNature', orderType);
    }

    if (token) params.append('token', token);
    if (type) params.append('type', type); // Market, Limit
    if (orderID) params.append('orderID', orderID);
    if (typeof active !== 'undefined' && active !== null) {
      params.append('active', active ? '1' : '0');
    }
    
    url += `?${params.toString()}`;
    const res = await fetch(url, { credentials: 'include' });
    let data = await res.json();
    
    // ... rest of the function remains the same
    
    // Filter by side if prop is provided
    let filteredData = data;
    if (side) {
      filteredData = filteredData.filter(order => order.side === side);
    }
    // Filter by order status if filterStatus prop is provided
    if (filterStatus) {
      filteredData = filteredData.filter(order => {
        if (!order.orderStatus) return false;
        // Case-insensitive match
        return order.orderStatus.toLowerCase() === filterStatus.toLowerCase();
      });
    }

    setOrders(filteredData);
    if (onOrderUpdate) {
      onOrderUpdate(filteredData);
    }
  } catch (err) {
    console.error('Error fetching orders:', err);
    toast.error('Failed to fetch orders');
  } finally {
    setLoading(false); // Make sure this is here
  }
};


  // useEffect(() => {
  //   // Fetch orders only
  //   const fetchData = async () => {
  //     await fetchOrders(false); // Pass false to show loading on initial/manual refresh
  //   };

  //   fetchData();
    // compute displayed orders based on normalFilter prop when viewing normal orders
    // If orderID prop is set, show all orders with _id === orderID or LinkedOrderId === orderID
    // const displayedOrders = useMemo(() => {
    //   if (orderID) {
    //     return orders.filter(
    //       o => o._id === orderID || o.LinkedOrderId === orderID
    //     );
    //   }
    //   // ...existing code...
    //   // (If you have custom normalFilter logic, keep it here)
    //   return orders;
    // }, [orders, orderID, orderType, filterMode]);
  // Effect removed as TP/SL counts now come from API

  // Edit button handler
  const handleEdit = (order) => {
    if (order.orderStatus === 'Filled' || order.orderStatus === 'filled') {
      toast.warn('Filled orders cannot be edited!', { 
        position: 'top-right', 
        autoClose: 5000 
      });
      return;
    }
    
    setEditingOrder(order);
    setOrderFormVisible(true);
  };

  // Handle order form success
  const handleOrderFormSuccess = () => {
    setEditingOrder(null);
    fetchOrders(); // Refresh orders table
    if (onOrderUpdate) {
      onOrderUpdate([]); // trigger parent refresh (OrdersPage will increment key)
    }
  };

  // Handle order form close
  const handleOrderFormClose = () => {
    setOrderFormVisible(false);
    setEditingOrder(null);
    setDcaOrder(null); // Clear DCA order data
  };

  // Handle DCA button click
  const handleDCAClick = (order) => {
    // Prepare DCA order data based on the original order
    const dcaData = {
      token: order.token,
      side: order.positionIdx === 1 ? 'Buy' : (order.positionIdx === 2 ? 'Sell' : order.side),
      orderType: order.orderType,
      stopLoss: order.temp_sl_input || '',
      Algorithm: order.Algorithm,
      positionIdx: order.positionIdx,
      DCA_for: order._id // Reference to original order
    };
    
    setDcaOrder(dcaData);
    setEditingOrder(null); // Clear any existing editing order
    setOrderFormVisible(true);
  };

  // Check if order was created within last 30 minutes
  const isOrderRecent = (createdAt) => {
    if (!createdAt) return false;
    const orderTime = new Date(createdAt);
    const currentTime = new Date();
    const thirtyMinutesAgo = new Date(currentTime.getTime() - 30 * 60 * 1000); // 30 minutes in milliseconds
    return orderTime >= thirtyMinutesAgo;
  };

  // Cancel order handler
  const handleCancelOrder = async (order) => {
    if (!order?.orderId) {
      toast.error('No orderId found');
      setCancelModal({ visible: false, order: null });
      setCancelComment(''); // Reset comment
      return;
    }
    
    // set per-order loading flag so UI can show inner spinner
    const key = order._id || order.orderId;
    setCancelLoadingMap(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${BASE_URL}/api/orders/cancel/${order.orderId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      
      if (data.bybitRes?.message === 'OK' || data.message === 'OK' || data.bybitRes?.retMsg === 'OK') {
        toast.success('Order cancelled');
        
        // Submit the comment to trade-comments (comment is required)
        try {
          const commentRes = await fetch(`${BASE_URL}/api/trade-comments`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              id: order.LinkedOrderId && order.LinkedOrderId.trim() !== "" 
    ? order.LinkedOrderId 
    : order._id,
              comment: `Order cancelled. Reason: ${cancelComment.trim()}`,
              images: [],
              toDo: false,
              reference_link: "",
              originalOrderId: order._id
            })
          });
          
          const commentData = await commentRes.json();
          if (commentData.ok) {
            console.log('Cancel comment added successfully');
          } else {
            console.error('Failed to add cancel comment:', commentData.error);
          }
        } catch (commentErr) {
          console.error('Error adding cancel comment:', commentErr);
        }
        
        await fetchOrders();
      } else {
        toast.error(cleanBybitFromMessage(data.bybitRes?.message) || cleanBybitFromMessage(data.message) || 'Cancel failed');
      }
    } catch (err) {
      console.error('Error cancelling order:', err);
      toast.error('Cancel failed');
    } finally {
      // clear loading and close modal
      setCancelLoadingMap(prev => ({ ...prev, [key]: false }));
      setCancelModal({ visible: false, order: null });
      setCancelComment(''); // Reset comment after operation
    }
  };

  // Add the getTheLatestStatus function to fetch and update order statuses
const getTheLatestStatus = async () => {
  try {
    // Step 1: Fetch all orders
    const response = await fetch(`${BASE_URL}/api/orders`, { credentials: "include" });
    const data = await response.json();

    if (!Array.isArray(data)) {
      console.error("Invalid response format. Expected an array.");
      return;
    }

    // Step 2: Filter orders with specific statuses
    const filteredOrders = data.filter(order => {
      const status = order.orderStatus?.toLowerCase();
      return (
        status !== "filled" &&
        status !== "cancelled" &&
        status !== "triggered" &&
        status !== "failed"
      );
    });
    // console.log(filteredOrders);

    // Step 3: Update status for active orders
    await Promise.all(
      filteredOrders.map(async (order) => {
        if (
          (order.orderStatus === "New" ||
            order.orderStatus === "placed" ||
            order.orderStatus === "Untriggered") &&
          order.orderId
        ) {
          try {
            // Fetch the latest status for the order
            await fetch(`${BASE_URL}/api/orders/status/${order.orderId}`, {
              credentials: "include",
            });
          } catch (e) {
            console.error(`Error fetching status for order ${order._id}:`, e);
          }
        }
      })
    );

    console.log("Order statuses updated successfully.");
  } catch (err) {
    console.error("Error in getTheLatestStatus:", err);
  }
};

// Add a useEffect hook to call getTheLatestStatus every 30 seconds
// useEffect(() => {
//   const interval = setInterval(() => {
//     getTheLatestStatus();
//   }, 30000); // 30 seconds

//   return () => clearInterval(interval); // Cleanup on component unmount
// }, []);

// Modify the useEffect hook to update orders in the background
// useEffect(() => {
//   const interval = setInterval(() => {
//     fetchOrders(true); // Pass true to indicate this is an auto-refresh
//   }, 30000); // 30 seconds

//   return () => clearInterval(interval); // Cleanup on component unmount
// }, []);

  // Table columns
  // Small helper component for numeric range filters (used in filterDropdown)
  const NumberRangeFilter = ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    const parts = (selectedKeys && selectedKeys[0]) ? selectedKeys[0].split(',') : ['', ''];
    const min = parts[0] || '';
    const max = parts[1] || '';

    return (
      <div style={{ padding: 8 }}>
        <Input
          placeholder="Min"
          value={min}
          onChange={(e) => {
            const v = e.target.value;
            setSelectedKeys([`${v},${max}`]);
          }}
          style={{ width: 120, marginBottom: 8, display: 'block' }}
        />
        <Input
          placeholder="Max"
          value={max}
          onChange={(e) => {
            const v = e.target.value;
            setSelectedKeys([`${min},${v}`]);
          }}
          style={{ width: 120, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button type="primary" onClick={() => confirm()} size="small">Apply</Button>
          <Button onClick={() => { clearFilters(); confirm(); }} size="small">Reset</Button>
        </Space>
      </div>
    );
  };

  // Build filter lists from available orders
  const tokenFilters = useMemo(() => {
    const s = Array.from(new Set(orders.map(o => (o.token || '').toString()).filter(Boolean)));
    return s.map(t => ({ text: t, value: t }));
  }, [orders]);

  const orderTypeFilters = useMemo(() => {
    const map = { tp: 'Take Profit', sl: 'Stop Loss', 'close_order': 'Closed Order', normal: 'Normal' };
    const s = Array.from(new Set(orders.map(o => (o.orderNature || 'normal').toString())));
    return s.map(v => ({ text: map[v] || v, value: v }));
  }, [orders]);

  const sideFilters = [
    { text: 'Long', value: 'Buy' },
    { text: 'Short', value: 'Sell' }
  ];

  const typeFilters = useMemo(() => {
    const s = Array.from(new Set(orders.map(o => (o.orderType || '').toString()).filter(Boolean)));
    return s.map(t => ({ text: t, value: t }));
  }, [orders]);

  const statusFilters = useMemo(() => {
    const s = Array.from(new Set(orders.map(o => (o.orderStatus || '').toString()).filter(Boolean)));
    return s.map(st => ({ text: st, value: st.toLowerCase() }));
  }, [orders]);

  // Helper to create onFilter for number ranges
  const numberRangeOnFilter = (dataIndex) => (value, record) => {
    if (!value) return true;
    const [minRaw, maxRaw] = (value || '').split(',');
    const min = minRaw !== undefined && minRaw !== '' ? Number(minRaw) : null;
    const max = maxRaw !== undefined && maxRaw !== '' ? Number(maxRaw) : null;
    const cell = record[dataIndex];
    const num = Number(cell == null || cell === '' ? 0 : cell);
    if (isNaN(num)) return false;
    if (min !== null && num < min) return false;
    if (max !== null && num > max) return false;
    return true;
  };




// createdAt wil be in JS code timeformat. extract hour, minute, second from it without spaceing. 

  // Format createdAt to IST and return string like "hh:mm-dd:MM"
  // const formatCreatedAtIST = (iso) => {
  //   if (!iso) return '';
  //   try {
  //     const dt = new Date(iso);
  //     const parts = new Intl.DateTimeFormat('en-GB', {
  //       timeZone: 'Asia/Kolkata',
  //       hour12: false,
  //       hour: '2-digit',
  //       minute: '2-digit',
  //       day: '2-digit',
  //       month: '2-digit',
  //       year: 'numeric'
  //     }).formatToParts(dt);
  //     const p = {};
  //     parts.forEach(({ type, value }) => { p[type] = value; });
  //     const hh = p.hour || '00';
  //     const mm = p.minute || '00';
  //     const dd = p.day || '00';
  //     const MM = p.month || '00';

  //     // return `${hh}:${mm}-${dd}:${MM}`; 
  //           return `${hh}:${mm}`;
  //   } catch (e) {
  //     return iso;
  //   }
  // };

  // Date range filter for createdAt (uses IST date for comparisons)
  const dateRangeOnFilter = (dataIndex) => (value, record) => {
    if (!value) return true;
    const [minRaw, maxRaw] = (value || '').split(','); // YYYY-MM-DD
    const created = record[dataIndex];
    if (!created) return false;
    try {
      const dt = new Date(created);
      // get IST date in YYYY-MM-DD via en-CA locale
      const istDate = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit' }).format(dt);
      if (minRaw && istDate < minRaw) return false;
      if (maxRaw && istDate > maxRaw) return false;
      return true;
    } catch (e) {
      return false;
    }
  };

  let columns = [
    { 
      title: 'S.No', 
      dataIndex: 'sno', 
      width: 80,
      
      render: (_, record, i) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ position: 'relative', display: 'inline-block', cursor: 'pointer', marginTop: 5 }} title="Trade Comments">
            <span onClick={e => {
              e.stopPropagation();
              const originalOrderId = record._id;
              const orderId = record.orderNature !== 'normal' ? record.LinkedOrderId : record._id;
              setCommentsModal({ visible: true, order: {...record, _id: orderId, originalOrderId} });
            }}>
              <svg width="20" height="16" fill={record.comment_added === false ? "#ffeded" : "none"} viewBox="0 0 20 20">
                <path d="M3 4.5A2.5 2.5 0 0 1 5.5 2h9A2.5 2.5 0 0 1 17 4.5v7A2.5 2.5 0 0 1 14.5 14H6.914a1 1 0 0 0-.707.293l-2.414 2.414A1 1 0 0 1 2 15.586V4.5Z" 
                  stroke={record.comment_added === false ? "#ff4d4f" : "#888"} 
                  strokeWidth="1.5" 
                  strokeLinejoin="round"
                />
              </svg>
              {todoCount[record._id] > 0 && (
                <span style={{
                  position: 'absolute',
                  top: -8,
                  right: -6,
                  background: '#000',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '18px',
                  height: '18px',
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}>
                  {todoCount[record._id]}
                </span>
              )}
            </span>
          </div>
          {i + 1}
          {/* ADD DCA button for normal orders placed within last 30 minutes and not already a DCA order */}
          {record && record.orderNature === 'normal' && isOrderRecent(record.createdAt) && !record.is_dca_order && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDCAClick(record);
              }}
              style={{
                marginLeft: 4,
                // marginTop: 12,
                padding: '2px 6px',
                fontSize: '10px',
                background: '#1890ff',
                color: 'white',
                border: 'none',
                borderRadius: '3px',
                cursor: 'pointer',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                display: 'inline-block'
              }}
              title="Add DCA Order"
            >
              + Add DCA
            </button>
          )}
          {/* Warning icon: show only for normal orders with slCount === 0 and status Filled */}
          {record && record.orderNature === 'normal' && (Number(record.slCount) === 0) && (record.remainingQuantity!=0) && ((record.orderStatus || '').toLowerCase() === 'filled') && (
            <span title="Stoploss Order Missing" style={{ marginLeft: 2, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L22 20H2L12 2Z" fill="#f59e0b" />
                <path d="M12 8v5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 16h.01" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </span>
      )
    },
    { 
      title: 'Token', 
      dataIndex: 'token',
      width: 100,
  filters: tokenFilters,
  filterSearch: (input, option) => option.value.toLowerCase().includes(input.toLowerCase()),
  onFilter: (value, record) => (record.token || '').toString() === value,
  filterDropdownPlacement: 'bottomLeft',
    },
    {
      title: 'Count',
      dataIndex: 'count',
      width: 150,
  filterDropdown: (props) => <NumberRangeFilter {...props} />,
  onFilter: numberRangeOnFilter('count'),
      render: (_, record) => {
        // if (userRole === 'admin') {
          // Admin: Show actual count with remaining quantity calculation
          const displayValue = record.count;
          let extraText = '';
          if (record.orderNature === 'normal' && record.remainingQuantity !== null) {
            extraText = ` - (${record.remainingQuantity})`;
          }
          return `${displayValue}${extraText}`;
        // } else {
        //   // Staff: Show simplified count (always 1 if > 0)
        //   return record.count;
        // }
      }
    },
    { 
      title: 'Order Type', 
      dataIndex: 'orderNature', 
      key: 'orderNature', 
      width: 120,
  filters: orderTypeFilters,
  onFilter: (value, record) => ((record.orderNature || 'normal') === value),
      render: val => {
        switch(val) {
          case 'tp': return 'Take Profit';
          case 'sl': return 'Stop Loss';
          case 'close_order': return 'Closed Order';
          default: return 'Trade open';
        }
      }
    },
    { 
      title: 'Side', 
      dataIndex: 'side',
      width: 100,
      filters: sideFilters,
      onFilter: (value, record) => (record.side === value),
      // Render depends on orderNature: show empty when not 'normal'
      render: (side, record) => {
        if (record && record.orderNature && record.orderNature !== 'normal') {
          // return empty string so the cell appears blank for non-normal orders
          return '';
        }

        return side === 'Buy' ? (
          <span style={{
            display: 'inline-block',
            padding: '2px 12px',
            borderRadius: '999px',
            border: '1px solid #22c55e',
            background: '#dcfce7',
            color: '#000',
            fontWeight: 600,
            fontSize: 12
          }}>Long</span>
        ) : (
          <span style={{
            display: 'inline-block',
            padding: '2px 12px',
            borderRadius: '999px',
            border: '1px solid #ef4444',
            background: '#fee2e2',
            color: '#000',
            fontWeight: 600,
            fontSize: 12
          }}>Short</span>
        );
      }
    },
    { 
      title: 'Type', 
      filters: typeFilters,
      onFilter: (value, record) => (record.orderType === value),
  dataIndex: 'orderType',
      render: t => (
        t === 'Limit' ? (
          <span style={{
            display: 'inline-block',
            padding: '2px 12px',
            borderRadius: '999px',
            background: '#000',
            color: '#fff',
            fontWeight: 600,
            fontSize: 12
          }}>Limit</span>
        ) : (
          <span style={{
            display: 'inline-block',
            padding: '2px 12px',
            borderRadius: '999px',
            background: '#fff',
            color: '#000',
            border: '1px solid #d1d5db',
            fontWeight: 600,
            fontSize: 12
          }}>Market</span>
        )
      ) 
    },
    { 
      title: 'Price', 
  dataIndex: 'entryPrice',
  filterDropdown: (props) => <NumberRangeFilter {...props} />,
  onFilter: numberRangeOnFilter('entryPrice'),
    },
    // {
    //   title: 'PnL',
    //   key: 'pnl',
    //   width: 120,
    //   render: (record) => {
    //     if (record.orderNature !== 'normal' || record.pnl === null || record.pnl === undefined) {
    //       return '';
    //     }

    //     if (userRole === 'admin') {
    //       // Admin: Show actual PnL value
    //       const pnl = record.pnl;
    //       const isProfit = pnl > 0;
    //       const display = Math.abs(pnl).toFixed(4);

    //       return (
    //         <span style={{
    //           display: 'inline-flex',
    //           alignItems: 'center',
    //           background: isProfit ? 'green' : 'red',
    //           color: 'white',
    //           borderRadius: 6,
    //           padding: '2px 10px',
    //           fontWeight: 600,
    //           fontSize: 13,
    //           minWidth: 80,
    //           justifyContent: 'center',
    //           gap: 4
    //         }}>
    //           {isProfit ? (
    //             <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{marginRight:2}} xmlns="http://www.w3.org/2000/svg">
    //               <path d="M8 12V4M8 4L4 8M8 4L12 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //           </svg>
    //           ) : (
    //             <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{marginRight:2}} xmlns="http://www.w3.org/2000/svg">
    //               <path d="M8 4v8m0 0l4-4m-4 4L4 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //           </svg>
    //           )}
    //           <div className="pnl-numbers">{display}</div>
    //         </span>
    //       );
    //     } else {
    //       // Staff: Show simplified PnL (1 for profit, -1 for loss)
    //       const pnl = record.pnl;
    //       const isProfit = pnl > 0;
    //       const display = isProfit ? '1' : '1'; // Show 1 for both profit and loss

    //       return (
    //         <span style={{
    //           display: 'inline-flex',
    //           alignItems: 'center',
    //           background: isProfit ? 'green' : 'red',
    //           color: 'white',
    //           borderRadius: 6,
    //           padding: '2px 10px',
    //           fontWeight: 600,
    //           fontSize: 13,
    //           minWidth: 80,
    //           justifyContent: 'center',
    //           gap: 4
    //         }}>
    //           {isProfit ? (
    //             <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{marginRight:2}} xmlns="http://www.w3.org/2000/svg">
    //               <path d="M8 12V4M8 4L4 8M8 4L12 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //           </svg>
    //           ) : (
    //             <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{marginRight:2}} xmlns="http://www.w3.org/2000/svg">
    //               <path d="M8 4v8m0 0l4-4m-4 4L4 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    //           </svg>
    //           )}
    //           <div className="pnl-numbers">{display}</div>
    //         </span>
    //       );
    //     }
    //   }
    // },
    {
  title: 'PnL',
  key: 'pnl',
  width: 120,
  filterDropdown: (props) => <NumberRangeFilter {...props} />,
  onFilter: numberRangeOnFilter('pnl'),
  render: (record) => {
    if (record.orderNature !== 'normal' || record.pnl === null || record.pnl === undefined) {
      return '';
    }

    // const pnl = record.pnl;
    // const isBuy = record.side === 'Buy';
    // // For Buy orders: positive PnL = profit (green/up), negative PnL = loss (red/down)
    // // For Sell orders: positive PnL = loss (red/down), negative PnL = profit (green/up)
    // const isProfit = isBuy ? pnl > 0 : pnl < 0;
    // const display = Math.abs(pnl).toFixed(4);

     const pnl = record.pnl;
    const isProfit = pnl > 0;
    const display = Math.abs(pnl).toFixed(4);

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          background: isProfit ? 'green' : 'red',
          color: 'white',
          borderRadius: 6,
          padding: '2px 10px',
          fontWeight: 600,
          fontSize: 13,
          minWidth: 80,
          justifyContent: 'center',
          gap: 4
        }}
      >
        {isProfit ? (
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            style={{ marginRight: 2 }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 12V4M8 4L4 8M8 4L12 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            style={{ marginRight: 2 }}
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8 4v8m0 0l4-4m-4 4L4 8"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        <div className="pnl-numbers">{display}</div>
      </span>
    );
  }
},
    {
      title: 'Order Status',
      dataIndex: 'orderStatus',
      width: 80,
  filters: statusFilters,
  filterSearch: (input, option) => option.value.toLowerCase().includes(input.toLowerCase()),
  onFilter: (value, record) => ((record.orderStatus || '').toLowerCase() === value),
      render: (status) => {
        if (status === 'New' || status === 'placed') {
          return (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 12px',
                borderRadius: '999px',
                background: '#000',
                color: '#fff',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {status}
            </span>
          );
        } else if (status === 'Filled' || status === 'filled') {
          return (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 12px',
                borderRadius: '999px',
                background: '#fff',
                color: '#000',
                border: '1px solid #d1d5db',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              {status}
            </span>
          );
        } else if (status === 'Cancelled' || status === 'Deactivated' || status === 'cancelled' || status === 'canceled') {
          return (
            <span
              style={{
                display: 'inline-block',
                padding: '2px 12px',
                borderRadius: '999px',
                background: '#e5e7eb',
                color: '#374151',
                border: '1px solid #d1d5db',
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              Cancelled
            </span>
          );
        } else {
          return <Tag>{status}</Tag>;
        }
      },
    }
  ];

  // Only show Take Profit column if at least one order is eligible
  const showTakeProfit = orders.some(order => 
    order.orderStatus && 
    order.orderStatus.toLowerCase() === 'filled' && 
    order.orderNature === 'normal'
  );
  
  if (showTakeProfit) {
    columns.push({
      title: 'TP',
      key: 'takeProfit',
      width: 70,
      render: (order) => (
        order.orderStatus &&
        order.orderStatus.toLowerCase() === 'filled' &&
        order.orderNature === 'normal' &&
        !shouldHideActionButtons(order) ? (
          <button
            style={{
              background: '#307d44ff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '3px 10px',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
              minWidth: 50,
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6
            }}
            onClick={async (e) => {
              e.stopPropagation();
              let currentPrice = '';
              try {
                const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${order.token}`);
                const data = await res.json();
                currentPrice = data?.result?.list?.[0]?.lastPrice || '';
              } catch (error) {
                console.error('Error fetching current price:', error);
              }
              setTpPopup({ visible: true, order, currentPrice });
            }}
          >
            TP
            {tpActiveCounts[order._id] > 0 && (
              <span style={{
                marginLeft: 0,
                background: '#000',
                color: '#fff',
                borderRadius: '50%',
                width: 18,
                height: 17,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                zIndex: 2
              }}>{tpActiveCounts[order._id]}</span>
            )}
          </button>
        ) : null
      )
    });
  }

  // Only show Stop Loss column if at least one order is eligible
  const showStopLoss = orders.some(order => 
    order.orderStatus && 
    order.orderStatus.toLowerCase() === 'filled' && 
    order.orderNature === 'normal'
  );
  
  if (showStopLoss) {
    columns.push({
      title: 'SL',
      key: 'stopLossAction',
      width: 70,
      render: (order) => (
        order.orderStatus &&
        order.orderStatus.toLowerCase() === 'filled' &&
        order.orderNature === 'normal' &&
        !shouldHideActionButtons(order) ? (
          <button
            style={{
              background: '#f92a2aff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '3px 10px',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer',
              minWidth: 50,
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6
            }}
            onClick={async (e) => {
              e.stopPropagation();
              setSlPopup({ visible: true, order, currentPrice: order.entryPrice || '' });
            }}
          >
            SL
            {slActiveCounts[order._id] > 0 && (
              <span style={{
                marginLeft: 0,
                background: '#000',
                color: '#fff',
                borderRadius: '50%',
                width: 18,
                height: 17,
                fontSize: 13,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                zIndex: 2
              }}>{slActiveCounts[order._id]}</span>
            )}
          </button>
        ) : null
      )
    });
  }

  // Add Cancel and Close columns
  columns.push({
    title: '',
    key: 'action',
    width: 180,
    render: (_, record) => {
      // Check if we should hide action buttons for this record
      const hideButtons = shouldHideActionButtons(record);
      
      // If buttons should be hidden, just show the emoji
      if (hideButtons) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getOrderEmoji(record, orders)}
          </div>
        );
      }

      // Show Close button for normal + Filled orders
      if (record.orderNature === 'normal' && record.orderStatus && record.orderStatus.toLowerCase() === 'filled') {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getOrderEmoji(record, orders)}
            <Button
              size="small"
              style={{ background: 'black', color: 'white', fontWeight: 600, border: 0, minWidth: 90, borderRadius: 6, padding: '4px 8px' }}
              onClick={() => handleOpenCloseModal(record)}
            >
              CLOSE {record.Algorithm ? `- ${record.Algorithm}` : ''}
            </Button>
          </div>
        );
      }
      // Show Cancel button for normal orders that are not Filled
      if (record.orderNature === 'normal' && record.orderStatus && record.orderStatus.toLowerCase() !== 'filled') {
        const key = record._id || record.orderId;
        const isLoading = !!cancelLoadingMap[key];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getOrderEmoji(record, orders)}
            <Button
              size="small"
              style={{ background: 'red', color: 'white', minWidth: 90, borderRadius: 6, padding: '4px 8px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => setCancelModal({ visible: true, order: record })}
              disabled={isLoading}
            >
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <SpinnerSmall size={14} color="#fff" />
                  <span style={{ fontWeight: 700 }}>Cancelling...</span>
                </span>
              ) : (
                `CANCEL${record.Algorithm ? ` - ${record.Algorithm}` : ''}`
              )}
            </Button>
          </div>
        );
      }
      // Show Cancel for all other non-normal orders
      if (record.orderNature !== 'normal') {
        const key = record._id || record.orderId;
        const isLoading = !!cancelLoadingMap[key];
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getOrderEmoji(record, orders)}
            <Button
              size="small"
              style={{ background: 'red', color: 'white', minWidth: 90, borderRadius: 6, padding: '4px 0', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
              onClick={() => setCancelModal({ visible: true, order: record })}
              disabled={isLoading}
            >
              {isLoading ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <SpinnerSmall size={14} color="#fff" />
                  <span style={{ fontWeight: 700 }}>Cancelling...</span>
                </span>
              ) : (
                `Cancel${record.Algorithm ? ` - ${record.Algorithm}` : ''}`
              )}
            </Button>
          </div>
        );
      }
      return <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {getOrderEmoji(record, orders)}
      </div>;
    }
  });

  // Add ID column (createdAt) after action
  columns.push({
    title: 'ID',
    dataIndex: '_id',
    width: 50,
    render: (_, record) => (
      record._id ? (
        <span style={{ fontSize: 15, color: '#555' }} title={record._id}>
          {record._id.slice(-8)}
        </span>
      ) : ''
    ),
    // date range filter (YYYY-MM-DD inputs)
    // filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
    //   const parts = (selectedKeys && selectedKeys[0]) ? selectedKeys[0].split(',') : ['', ''];
    //   const min = parts[0] || '';
    //   const max = parts[1] || '';
    //   return (
    //     <div style={{ padding: 8 }}>
    //       <div style={{ marginBottom: 8 }}>
    //         <label style={{ fontSize: 12 }}>From</label>
    //         <input
    //           type="date"
    //           value={min}
    //           onChange={e => setSelectedKeys([`${e.target.value},${max}`])}
    //           style={{ width: '100%', marginTop: 4 }}
    //         />
    //       </div>
    //       <div style={{ marginBottom: 8 }}>
    //         <label style={{ fontSize: 12 }}>To</label>
    //         <input
    //           type="date"
    //           value={max}
    //           onChange={e => setSelectedKeys([`${min},${e.target.value}`])}
    //           style={{ width: '100%', marginTop: 4 }}
    //         />
    //       </div>
    //       <Space>
    //         <Button type="primary" size="small" onClick={() => confirm()}>Apply</Button>
    //         <Button size="small" onClick={() => { clearFilters(); confirm(); }}>Reset</Button>
    //       </Space>
    //     </div>
    //   );
    // },
    // onFilter: dateRangeOnFilter('createdAt'),
    //chnage render value to the _id field
    // render: (val) => val._id || '',

  });

  // Add Created At column right after ID column
  columns.push({
    title: 'Created At',
    dataIndex: 'createdAt',
    width: 200,
    render: (createdAt, record) => {
      if (!createdAt) return '-';
      
      // Parse the ISO date string and convert to IST
      const date = new Date(createdAt);
      const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000)); // Add 5.5 hours for IST
      
      // Format components
      const day = istDate.getUTCDate().toString().padStart(2, '0');
      const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
      const month = months[istDate.getUTCMonth()];
      const hours = istDate.getUTCHours().toString().padStart(2, '0');
      const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
      const placedBy = record.placed_by || 'Unknown';
      
      return (
        <span style={{ 
          whiteSpace: 'nowrap', 
          display: 'inline-block',
          fontSize: '14px',
          lineHeight: '1.2'
        }}>
          {`${day} ${month} ${hours}:${minutes} ${placedBy}`}
        </span>
      );
    }
  });


  // --- Close Position Modal State ---
  const [closeModal, setCloseModal] = useState({ visible: false, order: null });
  const [closeQty, setCloseQty] = useState('');
  const [closeLoading, setCloseLoading] = useState(false);
  const [currentPrice, setCurrentPrice] = useState('');
  const [cancelLinkedOrders, setCancelLinkedOrders] = useState(false);


  // --- normalizeQuantity.js logic (inline for browser use) ---
  function countDecimals(str) {
    const s = String(str);
    const dot = s.indexOf(".");
    return dot === -1 ? 0 : (s.length - dot - 1);
  }
  function toScaledBigInt(value, scale) {
    const s = String(value);
    const decs = countDecimals(s);
    if (decs === scale) return BigInt(s.replace(".", ""));
    if (decs < scale) {
      const zeros = "0".repeat(scale - decs);
      return BigInt((s.includes(".") ? s.replace(".", "") : s) + zeros);
    }
    const [intPart, fracPart = ""] = s.split(".");
    const keep = fracPart.slice(0, scale);
    const drop = fracPart.slice(scale);
    let core = BigInt((intPart || "0") + (keep || "").padEnd(scale, "0"));
    if (drop && /[1-9]/.test(drop)) {
      core += 1n;
    }
    return core;
  }
  function ceilToStep(n, step) {
    return ((n + (step - 1n)) / step) * step;
  }
  async function normalizeQuantity(symbol, desiredQuantity) {
    if (!symbol || desiredQuantity == null) {
      throw new Error("symbol and desiredQuantity are required.");
    }
    const url = new URL("https://api.bybit.com/v5/market/instruments-info");
    url.searchParams.set("category", "linear");
    url.searchParams.set("symbol", symbol);
    let data;
    try {
      const res = await fetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      data = await res.json();
    } catch (err) {
      throw new Error(`Failed to fetch Bybit instruments info: ${err.message}`);
    }
    if (data?.retCode !== 0 || !data?.result?.list?.length) {
      throw new Error(`Unexpected API response: retCode=${data?.retCode}`);
    }
    const lot = data.result.list[0]?.lotSizeFilter;
    if (!lot) throw new Error("lotSizeFilter not found for symbol.");
    const minOrderQtyStr = lot.minOrderQty;
    const qtyStepStr     = lot.qtyStep;
    if (minOrderQtyStr == null || qtyStepStr == null) {
      throw new Error("minOrderQty or qtyStep missing in lotSizeFilter.");
    }
    const decMin  = countDecimals(minOrderQtyStr);
    const decStep = countDecimals(qtyStepStr);
    const scale   = Math.max(decMin, decStep);
    const stepScaled = toScaledBigInt(qtyStepStr, scale);
    if (stepScaled <= 0n) throw new Error("Invalid qtyStep <= 0.");
    const minScaled = toScaledBigInt(minOrderQtyStr, scale);
    let desiredScaled = toScaledBigInt(desiredQuantity, scale);
    let adjustedScaled = ceilToStep(desiredScaled, stepScaled);
    const minAligned = ceilToStep(minScaled, stepScaled);
    if (adjustedScaled < minAligned) adjustedScaled = minAligned;
    const adjustedStr = adjustedScaled.toString();
    let result;
    if (scale === 0) {
      result = Number(adjustedStr);
    } else {
      const intPart = adjustedStr.slice(0, -scale) || "0";
      const fracPart = adjustedStr.slice(-scale).padStart(scale, "0");
      result = Number(`${intPart}.${fracPart}`);
    }
    return result;
  }

  // Open close modal for a given order
  const handleOpenCloseModal = async (order) => {
    setCloseModal({ visible: true, order });
    setCloseQty('');
    setCancelLinkedOrders(false); // Reset checkbox when opening modal
    
    // Fetch current price
    try {
      const res = await fetch(`https://api.bybit.com/v5/market/tickers?category=linear&symbol=${order.token}`);
      const data = await res.json();
      const price = data?.result?.list?.[0]?.lastPrice || '';
      setCurrentPrice(price);
    } catch (error) {
      console.error('Error fetching current price:', error);
      setCurrentPrice('');
    }
  };

  // Small inline SVG spinner (no external CSS needed)
  const SpinnerSmall = ({ size = 14, color = '#fff' }) => (
    <svg width={size} height={size} viewBox="0 0 50 50" style={{ display: 'inline-block' }}>
      <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="5" strokeLinecap="round" strokeDasharray="31.415,31.415" transform="rotate(-90 25 25)">
        <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s" repeatCount="indefinite" />
      </circle>
    </svg>
  );

  // Helper: get available quantity based on user role
  // const getAvailableQty = (order) => {
  //   if (userRole === 'admin') {
  //     return order.remainingQuantity || 0;
  //   } else {
  //     // For staff, always return 1 if there's any quantity
  //     return order.count > 0 ? 1 : 0;
  //   }
  // };

  const getAvailableQty = (order) => {
      return order.remainingQuantity || 0;
  };

  // Submit close order
  const handleSubmitCloseOrder = async () => {
    const order = closeModal.order;
    if (!order) return;
    if (!closeQty || isNaN(closeQty) || Number(closeQty) <= 0) {
      toast.error('Enter a valid quantity');
      return;
    }
    
    // Validate against available quantity
    const availableQty = getAvailableQty(order);
    if (Number(closeQty) > availableQty) {
      toast.error('Cannot close more than available quantity');
      return;
    }

    // Validate and normalize quantity against Bybit requirements
    try {
      const normalizedQty = await normalizeQuantity(order.token, closeQty);
      
      // If normalized quantity is different from input, show warning
      if (normalizedQty !== Number(closeQty)) {
        toast.info(`Quantity adjusted to ${normalizedQty} to meet exchange requirements`, {
          position: 'top-right',
          autoClose: 5000
        });
      }
      
      // Update the closeQty with normalized value
      setCloseQty(normalizedQty.toString());
      
    } catch (err) {
      // Handle specific errors from Bybit API
      if (err.message.includes('minOrderQty')) {
        toast.error('Quantity is below minimum allowed amount', {
          position: 'top-right',
          autoClose: 5000
        });
      } else {
        toast.error('Error validating quantity: ' + err.message, {
          position: 'top-right',
          autoClose: 5000
        });
      }
      return;
    }
    
    setCloseLoading(true);

    // Side: reverse of original order
    const closeSide = order.side === 'Buy' ? 'Sell' : 'Buy';
    const ticker = order.token;
    const orderType = 'Market';
    const Algorithm = order.Algorithm || '';

    const payload = {
      token: ticker,
      side: closeSide,
      positionIdx: order.positionIdx,
      count: closeQty,
      orderType,
      algorithm: Algorithm,
      reduceOnly: true,
      orderNature: 'close_order',
      LinkedOrderId: order._id || null
    };

    try {
      const res = await fetch(`${BASE_URL}/api/orders/place`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      console.log('Close order response:', data);
      
      if (data && (data.message === 'OK' || data.bybitRes?.message === 'OK' || data.bybitRes?.retMsg === 'OK')) {
        toast.success('Close order placed!');
        
        // Show order success modal with all details
        openOrderSuccess({
          orderId: data._id,
          symbol: order.token,
          side: closeSide,
          positionIdx: order.positionIdx,
          isSuccess: true
        });
        
const orderId = data.bybitRes?.orderId || data.bybitRes?.api_response?.orderId || data.orderId;
        // console.log('Placed close order with ID:', orderId);
        if (orderId) {
          // Immediately fetch the order status
          try {
            const statusRes = await fetch(`${BASE_URL}/api/orders/status/${orderId}`, {
              credentials: 'include'
            });
            const statusData = await statusRes.json();
            // console.log('Fetched order status:', statusData);
            
            // Immediately fetch the latest order data from DB
            const orderRes = await fetch(`${BASE_URL}/api/orders/single/${order._id}`, {
              credentials: 'include'
            });
            const latestOrderData = await orderRes.json();
            // console.log('Fetched latest order data:', latestOrderData);
            
            // Update the specific order in UI with latest data from DB
            setOrders(prevOrders => prevOrders.map(o => {
              if (o._id === order._id && latestOrderData) {
                return {
                  ...o,
                  ...latestOrderData,
                  remainingQuantity: latestOrderData.remainingQuantity,
                  orderStatus: latestOrderData.orderStatus
                };
              }
              return o;
            }));
            
            // Calculate percentage used for TP/SL updates
            const availableQty = getAvailableQty(order);
            const percentageUsed = (Number(closeQty) / availableQty) * 100;
            
            // Call the new API to update TP/SL orders and add trade comments
            try {
              const updateRes = await fetch(`${BASE_URL}/api/orders/update-position-close/${order._id}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  percentage: percentageUsed,
                  qty: Number(closeQty),
                  cancelLinkedOrders: cancelLinkedOrders // Pass checkbox value
                }),
              });
              const updateData = await updateRes.json();
              
              if (updateData.success) {
                console.log('TP/SL orders updated successfully:', updateData);
              } else {
                console.error('Failed to update TP/SL orders:', updateData.error);
              }
            } catch (updateErr) {
              console.error('Error updating TP/SL orders:', updateErr);
            }
            
          } catch (err) {
            console.error('Error fetching order status:', err);
            // Fallback to basic quantity update
            setOrders(prevOrders => prevOrders.map(o => {
              if (o._id === order._id) {
                let newQty = (o.remainingQuantity || 0) - Number(closeQty);
                if (newQty < 0) newQty = 0;
                return { ...o, remainingQuantity: newQty };
              }
              return o;
            }));
          }
        } else {
          // Fallback to basic quantity update if no orderId
          setOrders(prevOrders => prevOrders.map(o => {
            if (o._id === order._id) {
              let newQty = (o.remainingQuantity || 0) - Number(closeQty);
              if (newQty < 0) newQty = 0;
              return { ...o, remainingQuantity: newQty };
            }
            return o;
          }));
        }

        setCloseModal({ visible: false, order: null });
        setCloseQty('');
        setCancelLinkedOrders(false); // Reset checkbox
        // Still refresh from backend for accuracy
        fetchOrders();
        if (onOrderUpdate) {
          onOrderUpdate([]); // trigger parent refresh (OrdersPage will increment key)
        }
      } else {
        const cleanedErrorMessage = cleanBybitFromMessage(data.message) || cleanBybitFromMessage(data.bybitRes?.message) || cleanBybitFromMessage(data.bybitRes?.retMsg) || cleanBybitFromMessage(data.error) || 'Failed to place close order';
        toast.error(cleanedErrorMessage);
        // Show order failure modal
        openOrderSuccess({
          symbol: order.token,
          side: closeSide,
          positionIdx: order.positionIdx,
          isSuccess: false,
          errorMessage: cleanedErrorMessage
        });
      }
    } catch (err) {
      console.error('Error placing close order:', err);
      toast.error('Failed to place close order');
      // Show order failure modal for catch errors
      openOrderSuccess({
        symbol: order.token,
        side: closeSide,
        positionIdx: order.positionIdx,
        isSuccess: false,
        errorMessage: 'Failed to place close order'
      });
    } finally {
      setCloseLoading(false);
    }
  };

  // compute displayed orders based on normalFilter prop when viewing normal orders
  const displayedOrders = useMemo(() => {
    const mode = filterMode || 'all';
    const nature = orderType || 'normal';

     if (orderID) {
        return orders.filter(
          o => o._id === orderID || o.LinkedOrderId === orderID
        );
      }

    if (nature === 'normal') {
      if (mode === 'active') {
        return Array.isArray(orders) ? orders.filter(o => (
          o && (o.is_active === true || o.is_active === 'true' || o.is_active === '1' || o.is_active === 1) && ((o.orderStatus || '').toLowerCase() !== 'new')
        )) : [];
      }
      if (mode === 'unfilled') {
        return Array.isArray(orders) ? orders.filter(o => (
          o && (o.is_active === true || o.is_active === 'true' || o.is_active === '1' || o.is_active === 1) && ((o.orderStatus || '').toLowerCase() !== 'filled')
        )) : [];
      }
      return orders;
    }

    if (nature === 'tp' || nature === 'sl') {
      if (mode === 'unfilled') {
        return Array.isArray(orders) ? orders.filter(o => (
          (o.orderStatus || '').toLowerCase() === 'untriggered'
        )) : [];
      }
      return orders;
    }

    if (nature === 'close_order') {
      if (mode === 'filled') {
        return Array.isArray(orders) ? orders.filter(o => (
          (o.orderStatus || '').toLowerCase() === 'filled'
        )) : [];
      }
      return orders;
    }

    return orders;
  }, [orders, orderType,orderID, filterMode]);

  return (
    <div>
      {/* Orders Table */}
      <Table
        dataSource={displayedOrders}
        columns={columns}
        rowKey="_id"
        pagination={false}
        bordered
        size="middle"
        style={{ background: 'white' }}
        scroll={{ x: 1200 }}
        loading={{
    spinning: loading,
    size: 'large',
    indicator: <Spin size="large" />
  }}
      />

      {/* Trade Comments Modal */}
      {/* {commentsModal.visible && commentsModal.order && (
        <TradeCommentsModal
          visible={commentsModal.visible}
          orderId={commentsModal.order._id}
          // username={getCurrentUser()}
          onClose={() => setCommentsModal({ visible: false, order: null })}
          onSaved={() => fetchOrders()}
        />
      )} */}
      {commentsModal.visible && commentsModal.order && (
  <TradeCommentsModal
    visible={commentsModal.visible}
    orderId={commentsModal.order._id}
    originalOrderId={commentsModal.order.originalOrderId}
    onClose={() => setCommentsModal({ visible: false, order: null })}
    onSaved={(data) => {
      // Update todo count immediately when a comment is added or marked as done
      setTodoCount(prev => ({
        ...prev,
        [commentsModal.order._id]: data.todoCount
      }));
      
      // Double-check the count from server to ensure accuracy
      fetch(`${BASE_URL}/api/trade-comments/${commentsModal.order._id}`, { 
        credentials: 'include' 
      })
        .then(res => res.json())
        .then(comments => {
          const count = comments.filter(c => c.toDo && !c.todo_completed).length;
          setTodoCount(prev => ({ ...prev, [commentsModal.order._id]: count }));
        });
    }}
  />
)}

      {/* Close Position Modal */}
      <Modal
        open={closeModal.visible}
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: 350 }}>
            <span>Close Order: {closeModal.order?.token || ''}</span>
            {closeModal.order && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: '#222' }}>Available Quantity:</span>
                <span style={{ fontWeight: 600, fontSize: 15, color: '#222', background: '#f5f5f5', borderRadius: 4, padding: '2px 8px' }}>
                  {getAvailableQty(closeModal.order)}
                </span>
                <Button
                  type="text"
                  size="small"
                  style={{ padding: 0, marginLeft: 2 }}
                  onClick={() => {
                    navigator.clipboard.writeText(getAvailableQty(closeModal.order).toString());
                    toast.info('Copied!');
                  }}
                  icon={
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1H13a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-1.5H4A1.5 1.5 0 0 1 2.5 9V3A1.5 1.5 0 0 1 4 1.5h6zm1.5 3V3A.5.5 0 0 0 11 2.5H4A.5.5 0 0 0 3.5 3v6A.5.5 0 0 0 4 9.5h.5V6a2 2 0 0 1 2-2h5zM6 5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H6z"/>
                    </svg>
                  }
                />
              </span>
            )}
          </div>
        }
        onCancel={() => setCloseModal({ visible: false, order: null })}
        footer={null}
        centered
      >
        {/* Percentage Buttons Row */}
        {closeModal.order && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            {[25, 40, 50, 75, 100].map(percent => (
              <Button
                key={percent}
                style={{ background: 'white', color: 'black', border: '1px solid #d1d5db', fontWeight: 600, minWidth: 48, borderRadius: 6 }}
                onClick={async () => {
                  const avail = getAvailableQty(closeModal.order);
                  if (avail > 0) {
                    const rawQty = ((avail * percent) / 100).toFixed(4);
                    try {
                      const normalized = await normalizeQuantity(closeModal.order.token, rawQty);
                      setCloseQty(normalized.toString());
                    } catch (err) {
                      toast.error('Error normalizing quantity: ' + err.message);
                    }
                  }
                }}
              >
                {percent}%
              </Button>
            ))}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontWeight: 500 }}>Enter Quantity to Close:</span>
          <input
            type="number"
            min={0.0001}
            step={0.0001}
            value={closeQty}
            onChange={e => {
              const val = e.target.value;
              if (!val) {
                setCloseQty('');
                return;
              }
              setCloseQty(val);
            }}
            style={{ marginTop: 8, width: '100%', padding: 6, borderRadius: 4, border: '1px solid #d1d5db' }}
            placeholder="Quantity"
            autoFocus
          />
        </div>
        
        <div style={{ marginBottom: 16 }}>
          <Checkbox
            checked={cancelLinkedOrders}
            onChange={(e) => setCancelLinkedOrders(e.target.checked)}
          >
            Cancel all linked TP/SL orders after closing position
          </Checkbox>
        </div>
        <Button
            type="primary"
            style={{ background: 'black', color: 'white', fontWeight: 600, width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
            onClick={handleSubmitCloseOrder}
            disabled={closeLoading}
          >
            {closeLoading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SpinnerSmall size={16} color="#fff" />
                <span style={{ fontWeight: 700 }}>Closing...</span>
              </span>
            ) : (
              `Close Position - ${closeModal.order?.token || ''} ${closeModal.order?.Algorithm ? `- ${closeModal.order.Algorithm}` : ''}`
            )}
          </Button>
      </Modal>

      {/* Cancel Modal */}
      <Modal
        open={cancelModal.visible}
        title="Confirm Cancel Order"
        onCancel={() => {
          setCancelModal({ visible: false, order: null });
          setCancelComment(''); // Reset comment when closing
        }}
        footer={null}
        centered
      >
        <div style={{ marginBottom: 16, fontWeight: 500, fontSize: 16 }}>
          Are you sure you want to cancel this order?
        </div>
        
        {/* Comment input field */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            Cancel Reason (Required):
          </label>
          <Input.TextArea
            value={cancelComment}
            onChange={(e) => setCancelComment(e.target.value)}
            placeholder="Enter reason for cancellation..."
            rows={3}
            maxLength={500}
            showCount
            required
            status={cancelComment.trim() === '' ? 'error' : ''}
          />
          {/* {cancelComment.trim() === '' && (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
              Please provide a reason for cancellation
            </div>
          )} */}
        </div>
        
        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
          <Button 
            onClick={() => {
              setCancelModal({ visible: false, order: null });
              setCancelComment(''); // Reset comment when clicking No
            }} 
            style={{ minWidth: 80 ,marginTop:5}}
          >
            No
          </Button>
          <Button
            type="primary"
            danger
            style={{ minWidth: 140, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,marginTop:5 }}
            onClick={() => handleCancelOrder(cancelModal.order)}
            disabled={!!cancelLoadingMap[cancelModal.order?._id || cancelModal.order?.orderId] || cancelComment.trim() === ''}
          >
            {cancelLoadingMap[cancelModal.order?._id || cancelModal.order?.orderId] ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <SpinnerSmall size={16} color="#fff" />
                <span style={{ fontWeight: 700 }}>Cancelling...</span>
              </span>
            ) : (
              'Yes, Cancel'
            )}
          </Button>
        </Space>
      </Modal>

      {/* Order Form Modal */}
      <OrderForm
        visible={orderFormVisible}
        onClose={handleOrderFormClose}
        onOrderSuccess={handleOrderFormSuccess}
        editingOrder={editingOrder}
        dcaOrder={dcaOrder}
      />

      {/* Take Profit Popup */}
      {typeof TakeProfitPopup !== 'undefined' && (
        <TakeProfitPopup
          visible={tpPopup.visible}
          order={tpPopup.order}
          currentPrice={tpPopup.currentPrice}
          onClose={() => {
            setTpPopup({ visible: false, order: null, currentPrice: '' });
            if (tpPopup.order && tpPopup.order._id) refreshTpSlCountForOrder(tpPopup.order._id);
          }}
          fetchOrders={() => {
            fetchOrders(false, true); // Skip loading when called from TP popup
            if (tpPopup.order && tpPopup.order._id) refreshTpSlCountForOrder(tpPopup.order._id);
          }}
        />
      )}

      {/* Stop Loss Popup */}
      {typeof StopLossPopup !== 'undefined' && (
        <StopLossPopup
          visible={slPopup.visible}
          order={slPopup.order}
          currentPrice={slPopup.currentPrice}
          onClose={() => {
            setSlPopup({ visible: false, order: null, currentPrice: '' });
            if (slPopup.order && slPopup.order._id) refreshTpSlCountForOrder(slPopup.order._id);
          }}
          fetchOrders={() => {
            fetchOrders(false, true); // Skip loading when called from SL popup
            if (slPopup.order && slPopup.order._id) refreshTpSlCountForOrder(slPopup.order._id);
          }}
        />
      )}
    </div>
  );
};

export default OrdersTable;